import React, { useState } from 'react';
import { MdCheck } from '@react-icons/all-files/md/MdCheck';
import { MdAdd } from '@react-icons/all-files/md/MdAdd';
import { MdClose } from '@react-icons/all-files/md/MdClose';

/**
 * ImageRadioButtonGroup component
 *
 * A functional component that renders an image-based button group. It can behave
 * as either a single-select (radio button) or multi-select (checkbox) based on the `multiSelect` prop.
 * The component allows the user to select between different options represented by images.
 *
 * @param {string} label - The text label displayed for each image button.
 * @param {string} value - The unique value associated with each image button.
 * @param {Array|string} selectedValue - The currently selected value(s). It can be a string for single-select or an array for multi-select.
 * @param {function} onChange - Callback function called when a selection is made. In single-select mode, it passes the selected value; in multi-select mode, it passes an array of selected values.
 * @param {string} image - The URL or path to the image displayed in each button.
 * @param {boolean} [multiSelect=false] - Determines if the component allows multiple selections (checkbox) or a single selection (radio button). Defaults to single-select (false).
 * @param {string} type -  The type select items icon e.g. default se;ect ('normal'), add Option select ('add') or remove option select ('remove').
 *
 * @returns {JSX.Element} - The rendered image-based button group.
 */
const ImageRadioButton = ({
  label,
  value,
  selectedValue,
  onChange,
  image,
  multiSelect = true,
  type = 'normal',
  onHoverImage = null,
  isDisabled = false
}) => {
  const isSelected = multiSelect
    ? selectedValue.includes(value)
    : selectedValue === value;

  const handleClick = () => {
    if (multiSelect) {
      const updatedSelection = isSelected
        ? selectedValue.filter((item) => item !== value)
        : [...selectedValue, value];
      onChange(updatedSelection);
    } else {
      onChange(value);
    }
  };

  const [selectedImage, setSelectedImage] = useState(image)

  //Class lists
  const parentContainerClasslist = (
    `image-radio-buttons-wrapper
    ${isSelected ? 'image-radio-buttons-wrapper--is-selected' : ''}
    ${isSelected && type == 'remove' ? 'image-radio-buttons-wrapper--is-selected--is-warning' : ''}
    ${isDisabled && isSelected ?
      // Case 1: The item is both disabled AND selected. Apply a specific class for this state.
      'image-radio-buttons-wrapper--is-selected--is-disabled' :
      // Case 2: The item is disabled BUT NOT selected. Apply a general disabled class.
      isDisabled && !isSelected ? 'image-radio-buttons-wrapper--is-disabled' :
        // If the item is not disabled at all, add no class from this condition.
        ''}
  `)

  const markerCircleClasslist = (
    `radio-circle-outline 
    ${isSelected ?
      type == 'remove' ?
        'radio-circle-outline--is-selected--warning' :
        'radio-circle-outline--is-selected' : ''}
      ${isDisabled && isSelected ?
      // Case 1: The item is both disabled AND selected. Apply a specific class for this state.
      'radio-circle-outline--is-selected--is-disabled' :
      // Case 2: The item is disabled BUT NOT selected. Apply a general disabled class.
      isDisabled && !isSelected ? 'radio-circle-outline--is-disabled' :
        // If the item is not disabled at all, add no class from this condition.
        ''}
          `)

  const imageOverlayClasslist = (
    `image-radio-buttons-image-overlay
    ${type == 'remove' ? 'image-radio-buttons-image-overlay--warning' : ''}
    ${isDisabled && isSelected ?
      // Case 1: The item is both disabled AND selected. Apply a specific class for this state.
      'image-radio-buttons-image-overlay--is-selected--is-disabled' :
      // Case 2: The item is disabled BUT NOT selected. Apply a general disabled class.
      isDisabled && !isSelected ? 'image-radio-buttons-image-overlay--is-disabled' :
        // If the item is not disabled at all, add no class from this condition.
        ''}
      `)

  const labelClassList = (`image-radio-buttons-text ${label.length > 16 ? 'image-radio-buttons-text--small-font' : ''}`)

  return (
    <div
      onClick={handleClick}
      className={parentContainerClasslist}
      onMouseOver={() => onHoverImage ? setSelectedImage(onHoverImage) : null}
      onMouseOut={() => { setSelectedImage(image) }}
    >
      <div className={'image-radio-buttons-marker'}>
        {isSelected && type == 'normal' && <MdCheck className='image-radio-buttons-icon' />}
        {isSelected && type == 'add' && <MdAdd className='image-radio-buttons-icon' />}
        {isSelected && type == 'remove' && <MdClose className='image-radio-buttons-icon' />}
        <div className={markerCircleClasslist}></div>
      </div>
      <div className={'image-radio-buttons-image-container'}>
        <img
          className={'image-radio-buttons-image'}
          src={selectedImage}
          alt={`${label}-image`}
        />
        {isSelected && <div className={imageOverlayClasslist}></div>}
      </div>
      <span className={labelClassList}>
        {label}
      </span>
    </div>
  );
};

export default ImageRadioButton;
